import { put, call } from 'redux-saga/effects';
import { IdentityActions } from '../Redux/IdentityRedux';
import { IdentityServiceType } from '../Services/IdentityService';
import { ApiResponse } from 'apisauce';
import { UserReadonly } from 'Models/Identity/User';
import PasswordReset from 'Models/Identity/PasswordReset';
import UnlockUser from 'Models/Identity/UnlockUser';
import MfaGlobalConfig from 'Models/Identity/MfaGlobalConfig';
import MfaUserExtension from 'Models/Identity/MfaUserExtension';
import MfaDetailsResponse from 'Models/Identity/MfaDetailsResponse';
import MfaStatistics from 'Models/Identity/MfaStatistics';
import MfaTrustedDevice from 'Models/Identity/MfaTrustedDevice';

interface IdentityParams {
   type: string;
   token: string;
   user: UserReadonly;
   data: PasswordReset;
   userId: string;
   subjectId: string;
   newMethod: string;
   enforcementDate: string | null;
   signinBannerMessage: string | null;
   id: string;
}

export function* postPasswordReset(
   identityService: Readonly<IdentityServiceType>,
   action: Readonly<IdentityParams>
): Generator {
   const { user } = action;
   const identityResponse = yield call(identityService.postPasswordReset, user);
   const response = identityResponse as ApiResponse<string>;

   let passwordReset: PasswordReset = { message: '', success: false };
   if (response.data) {
      passwordReset = {
         ...passwordReset,
         message: response.data.toString(),
      };
   }

   if (response.status && response.ok && response.data) {
      passwordReset = { ...passwordReset, success: true };
      yield put(IdentityActions.postPasswordResetSuccess(passwordReset));
   } else {
      yield put(
         IdentityActions.postPasswordResetFailure(
            {
               code: 'Error',
               status: response.status,
            },
            passwordReset
         )
      );
   }
}

export function* postMfaReset(
   identityService: Readonly<IdentityServiceType>,
   action: Readonly<IdentityParams>
): Generator {
   const response = (yield call(
      identityService.postMfaReset,
      action.userId
   )) as ApiResponse<PasswordReset>;

   const data = response?.data as PasswordReset | undefined;
   const success = data?.success || response?.ok || false;
   const message =
      data?.message || (response?.ok ? 'MFA has been reset.' : 'Unknown error');

   const mfaReset: PasswordReset = { success, message };

   if (response?.ok) {
      yield put(IdentityActions.postMfaResetSuccess(mfaReset));
   } else {
      yield put(
         IdentityActions.postMfaResetFailure(
            {
               code: 'Error',
               status: response?.status,
            },
            mfaReset
         )
      );
   }
}

export function* getMfaConfig(
   identityService: Readonly<IdentityServiceType>
): Generator {
   const response = (yield call(identityService.getMfaConfig)) as ApiResponse<
      MfaGlobalConfig
   >;

   if (response?.ok && response.data) {
      yield put(IdentityActions.getMfaConfigSuccess(response.data));
   } else {
      yield put(
         IdentityActions.getMfaConfigFailure({
            code: 'Error',
            status: response?.status,
         })
      );
   }
}

export function* getMfaDetails(
   identityService: Readonly<IdentityServiceType>,
   action: Readonly<IdentityParams>
): Generator {
   const response = (yield call(
      identityService.getMfaDetails,
      action.subjectId
   )) as ApiResponse<MfaUserExtension>;

   if (response?.ok && response.data) {
      yield put(IdentityActions.getMfaDetailsSuccess(response.data));
   } else {
      yield put(
         IdentityActions.getMfaDetailsFailure({
            code: 'Error',
            status: response?.status,
         })
      );
   }
}

export function* deleteMfaSmsEnrollment(
   identityService: Readonly<IdentityServiceType>,
   action: Readonly<IdentityParams>
): Generator {
   const response = (yield call(
      identityService.deleteMfaSmsEnrollment,
      action.subjectId
   )) as ApiResponse<string>;

   const result: PasswordReset = {
      success: response?.ok || false,
      message:
         response?.data?.toString() ||
         (response?.ok ? '' : 'An error occurred'),
   };

   if (response?.ok) {
      yield put(IdentityActions.deleteMfaSmsEnrollmentSuccess(result));
   } else {
      yield put(
         IdentityActions.deleteMfaSmsEnrollmentFailure(
            { code: 'Error', status: response?.status },
            result
         )
      );
   }
}

export function* deleteMfaAuthenticatorEnrollment(
   identityService: Readonly<IdentityServiceType>,
   action: Readonly<IdentityParams>
): Generator {
   const response = (yield call(
      identityService.deleteMfaAuthenticatorEnrollment,
      action.subjectId
   )) as ApiResponse<string>;

   const result: PasswordReset = {
      success: response?.ok || false,
      message:
         response?.data?.toString() ||
         (response?.ok ? '' : 'An error occurred'),
   };

   if (response?.ok) {
      yield put(
         IdentityActions.deleteMfaAuthenticatorEnrollmentSuccess(result)
      );
   } else {
      yield put(
         IdentityActions.deleteMfaAuthenticatorEnrollmentFailure(
            { code: 'Error', status: response?.status },
            result
         )
      );
   }
}

export function* patchMfaDefaultMethod(
   identityService: Readonly<IdentityServiceType>,
   action: Readonly<IdentityParams>
): Generator {
   const response = (yield call(
      identityService.patchMfaDefaultMethod,
      action.subjectId,
      action.newMethod
   )) as ApiResponse<string>;

   const result: PasswordReset = {
      success: response?.ok || false,
      message:
         response?.data?.toString() ||
         (response?.ok ? '' : 'An error occurred'),
   };

   if (response?.ok) {
      yield put(IdentityActions.patchMfaDefaultMethodSuccess(result));
   } else {
      yield put(
         IdentityActions.patchMfaDefaultMethodFailure(
            { code: 'Error', status: response?.status },
            result
         )
      );
   }
}

export function* getMyMfaDetails(
   identityService: Readonly<IdentityServiceType>
): Generator {
   const response = (yield call(
      identityService.getMyMfaDetails
   )) as ApiResponse<MfaDetailsResponse>;

   if (response?.ok && response.data) {
      yield put(IdentityActions.getMyMfaDetailsSuccess(response.data));
   } else {
      yield put(
         IdentityActions.getMyMfaDetailsFailure({
            code: 'Error',
            status: response?.status,
         })
      );
   }
}

export function* getMfaStatistics(
   identityService: Readonly<IdentityServiceType>
): Generator {
   const response = (yield call(
      identityService.getMfaStatistics
   )) as ApiResponse<MfaStatistics>;

   if (response?.ok && response.data) {
      yield put(IdentityActions.getMfaStatisticsSuccess(response.data));
   } else {
      yield put(
         IdentityActions.getMfaStatisticsFailure({
            code: 'Error',
            status: response?.status,
         })
      );
   }
}

export function* updateMfaEnforcementDateAndMessage(
   identityService: Readonly<IdentityServiceType>,
   action: Readonly<IdentityParams>
): Generator {
   const response = (yield call(
      identityService.updateMfaEnforcementDateAndMessage,
      action.enforcementDate,
      action.signinBannerMessage
   )) as ApiResponse<MfaGlobalConfig>;

   if (response?.ok && response.data) {
      yield put(
         IdentityActions.updateMfaEnforcementDateAndMessageSuccess(
            response.data
         )
      );
   } else {
      yield put(
         IdentityActions.updateMfaEnforcementDateAndMessageFailure({
            code: 'Error',
            status: response?.status,
         })
      );
   }
}

export function* getTrustedDevices(
   identityService: Readonly<IdentityServiceType>
): Generator {
   const response = (yield call(
      identityService.getTrustedDevices
   )) as ApiResponse<MfaTrustedDevice[]>;

   if (response?.ok && response.data) {
      yield put(IdentityActions.getTrustedDevicesSuccess(response.data));
   } else {
      yield put(
         IdentityActions.getTrustedDevicesFailure({
            code: 'Error',
            status: response?.status,
         })
      );
   }
}

export function* getTrustedDeviceById(
   identityService: Readonly<IdentityServiceType>,
   action: Readonly<IdentityParams>
): Generator {
   const response = (yield call(
      identityService.getTrustedDeviceById,
      action.id
   )) as ApiResponse<MfaTrustedDevice>;

   if (response?.ok && response.data) {
      yield put(IdentityActions.getTrustedDeviceByIdSuccess(response.data));
   } else {
      yield put(
         IdentityActions.getTrustedDeviceByIdFailure({
            code: 'Error',
            status: response?.status,
         })
      );
   }
}

export function* revokeDevice(
   identityService: Readonly<IdentityServiceType>,
   action: Readonly<IdentityParams>
): Generator {
   const response = (yield call(
      identityService.revokeDevice,
      action.id
   )) as ApiResponse<string>;

   const result: PasswordReset = {
      success: response?.ok || false,
      message:
         response?.data?.toString() ||
         (response?.ok ? '' : 'An error occurred'),
   };

   if (response?.ok) {
      yield put(IdentityActions.revokeDeviceSuccess(result));
   } else {
      yield put(
         IdentityActions.revokeDeviceFailure(
            { code: 'Error', status: response?.status },
            result
         )
      );
   }
}

export function* postUnlockUser(
   identityService: Readonly<IdentityServiceType>,
   action: Readonly<IdentityParams>
): Generator {
   const response = (yield call(
      identityService.postUnlockUser,
      action.userId
   )) as ApiResponse<UnlockUser>;

   const data = response?.data;
   const success = data?.success || false;
   const message = data?.message || 'Unknown error';

   const unlockUser: UnlockUser = { success, message };

   if (response?.ok && success) {
      yield put(IdentityActions.postUnlockUserSuccess(unlockUser));
   } else {
      yield put(
         IdentityActions.postUnlockUserFailure(
            {
               code: 'Error',
               status: response?.status,
            },
            unlockUser
         )
      );
   }
}
